import { Component, EventEmitter, OnInit } from '@angular/core';
import { AccountGroupService } from '../account-group.service';
import { AccountGroup } from '../account-group';
import { NotificationService } from '../../../shared/notification.service';

@Component({
  selector: 'app-account-group-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class AccountGroupListComponent implements OnInit {
  public accountGroup: AccountGroup;

  /**
   * A list of all the account-groups currently loaded.
   *
   * @type {AccountGroup[]}
   */
  public accountGroups: AccountGroup[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of account-groups.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of account-groups that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of account-groups that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a accountGroup has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public accountGroupDeleted: EventEmitter<boolean> = new EventEmitter<
    boolean
  >();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {AccountGroupService} accountGroupService
   * @param {NotificationService} notificationService
   */
  constructor(
    private accountGroupService: AccountGroupService,
    private notificationService: NotificationService,
  ) {
    this.accountGroupService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.accountGroupService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.accountGroupService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.accountGroupService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the accountGroup list.
   */
  public ngOnInit(): void {
    this.loadAccountGroups(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * accountGroup list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadAccountGroups(pageNumber);
  }

  /**
   * Loads a list of account-groups using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadAccountGroups(page?: number, limit?: number): void {
    const listAccountGroups = this.accountGroupService
      .listAccountGroups(page, limit)
      .subscribe((accountGroups: AccountGroup[]) => {
        this.accountGroups = accountGroups;
        listAccountGroups.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate accountGroup ID
   * to delete the accountGroup.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.accountGroupService.deleteAccountGroup(id).subscribe(
      deleted => {
        this.notificationService.success(
          `AccountGroup Deleted`,
          `AccountGroup with ID ${id} was deleted.`,
          5,
        );
        this.accountGroupDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `AccountGroup Not Deleted`,
          err.error.message,
          10,
        );
        this.accountGroupDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
